import React, { useState, useRef } from "react";
import { Card, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faTrashCan, faImage, faCalendar, faLocationDot, faTag, faGlobe, faPhotoFilm } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import api from "../api";
import CoverPickerModal from "./CoverPickerModal";

const DetailRow = ({ icon, label, value, color = "#6366f1", bg = "#eef2ff" }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: "12px",
    padding: "10px 14px", borderRadius: "10px",
    background: "#fafafa", border: "1px solid #f0f0f0",
  }}>
    <div style={{ width: 30, height: 30, borderRadius: "8px", background: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <FontAwesomeIcon icon={icon} style={{ color, fontSize: "13px" }} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: "10px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      <div style={{ fontSize: "13px", color: "#374151", fontWeight: 500, marginTop: "1px" }}>{value || "—"}</div>
    </div>
  </div>
);

const IconBtn = ({ onClick, icon, title, danger = false, disabled = false, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      display: "flex", alignItems: "center", gap: "6px",
      height: 32, padding: "0 12px",
      borderRadius: "8px",
      border: `1px solid ${danger ? "#fca5a5" : "#e5e7eb"}`,
      background: "#fff",
      color: danger ? "#ef4444" : "#6b7280",
      cursor: disabled ? "not-allowed" : "pointer",
      fontSize: "12px", fontWeight: 600,
      opacity: disabled ? 0.6 : 1,
      transition: "all 0.15s",
    }}
    onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = danger ? "#fff5f5" : "#f3f4f6"; }}
    onMouseLeave={e => { if (!disabled) e.currentTarget.style.background = "#fff"; }}
  >
    <FontAwesomeIcon icon={icon} style={{ fontSize: "12px" }} />
    {children}
  </button>
);

const EventDetails = ({ event, typeList, onEdit, onDelete, onCoverUpdate }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showCoverPicker, setShowCoverPicker] = useState(false);
  const [coverPreview, setCoverPreview] = useState(
    event?.cover_image_url ? `${event.cover_image_url}&t=${Date.now()}` : null
  );
  const [eventLanguage, setEventLanguage] = useState(event?.language || null);
  const [savingLang, setSavingLang] = useState(false);

  if (!event)
    return (
      <div className="text-center text-muted py-4">
        <p className="mb-0">{t("events_admin.select_event")}</p>
      </div>
    );

  const type = typeList.find((t) => t.type_id === event.type_id)?.type_name;

  const handleCoverChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverPreview(URL.createObjectURL(file));
    const formData = new FormData();
    formData.append("cover", file);
    setUploading(true);
    try {
      const res = await api.post(`/event_cover?eid=${event.event_id}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (onCoverUpdate) onCoverUpdate(res.data.cover_image_url);
    } catch (err) {
      console.error("Cover upload failed:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleLanguageChange = async (lang) => {
    setSavingLang(true);
    try {
      await api.patch(`/event/${event.event_id}/language`, { language: lang });
      setEventLanguage(lang);
    } catch (err) {
      console.error("Language update failed:", err);
    } finally {
      setSavingLang(false);
    }
  };

  const handleCoverDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/event_cover?eid=${event.event_id}`);
      setCoverPreview(null);
      if (onCoverUpdate) onCoverUpdate(null);
    } catch (err) {
      console.error("Cover delete failed:", err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
    <Card
      className="mt-3"
      style={{ border: "none", borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06)", overflow: "hidden" }}
    >
      <Card.Body style={{ padding: "20px 24px" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {t("events_admin.details_title")}
          </div>
          <div style={{ display: "flex", gap: "6px" }}>
            <IconBtn onClick={() => onEdit(event)} icon={faPenToSquare}>
              {t("events_admin.edit_event")}
            </IconBtn>
            <IconBtn onClick={() => onDelete(event)} icon={faTrashCan} danger>
              {t("events_admin.delete_event")}
            </IconBtn>
          </div>
        </div>

        {/* Detail rows */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <DetailRow icon={faCalendar} label={t("events_admin.date")}  value={event.date}              color="#6366f1" bg="#eef2ff" />
          <DetailRow icon={faLocationDot} label={t("events_admin.venue")} value={event.event_room}    color="#ec4899" bg="#fdf2f8" />
          <DetailRow icon={faTag}      label={t("events_admin.type")}  value={type}                   color="#f59e0b" bg="#fffbeb" />
        </div>

        {/* Cover */}
        <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #f0f0f0" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "12px" }}>
            {t("events_admin.cover_section")}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {coverPreview && (
              <img
                src={coverPreview}
                alt="cover"
                style={{ height: 60, width: 96, objectFit: "cover", borderRadius: "10px", border: "1px solid #f0f0f0" }}
              />
            )}
            <input ref={fileInputRef} type="file" accept="image/*" className="d-none" onChange={handleCoverChange} />
            <IconBtn onClick={() => fileInputRef.current.click()} icon={faImage} disabled={uploading || deleting}>
              {uploading ? <Spinner size="sm" animation="border" /> : (coverPreview ? t("events_admin.cover_change") : t("events_admin.cover_add"))}
            </IconBtn>
            <IconBtn onClick={() => setShowCoverPicker(true)} icon={faPhotoFilm} disabled={uploading || deleting}>
              {t("events_admin.cover_pick_from_gallery")}
            </IconBtn>
            {coverPreview && (
              <IconBtn onClick={handleCoverDelete} icon={faTrashCan} danger disabled={uploading || deleting}>
                {deleting ? <Spinner size="sm" animation="border" /> : null}
              </IconBtn>
            )}
          </div>
        </div>

        {/* Language */}
        <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #f0f0f0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
            <div style={{ width: 24, height: 24, borderRadius: "6px", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FontAwesomeIcon icon={faGlobe} style={{ color: "#22c55e", fontSize: "11px" }} />
            </div>
            <span style={{ fontSize: "11px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {t("events_admin.language_title")}
            </span>
          </div>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {[
              { value: null,  label: t("events_admin.language_default") },
              { value: "en",  label: "🇬🇧 EN" },
              { value: "fr",  label: "🇫🇷 FR" },
              { value: "he",  label: "🇮🇱 HE" },
            ].map(({ value, label }) => {
              const active = eventLanguage === value;
              return (
                <button
                  key={String(value)}
                  onClick={() => !savingLang && handleLanguageChange(value)}
                  disabled={savingLang}
                  style={{
                    height: 30, padding: "0 12px",
                    borderRadius: "8px",
                    border: `1px solid ${active ? "#6366f1" : "#e5e7eb"}`,
                    background: active ? "#eef2ff" : "#fff",
                    color: active ? "#6366f1" : "#6b7280",
                    fontSize: "12px", fontWeight: active ? 700 : 500,
                    cursor: savingLang ? "wait" : "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "8px" }}>
            {t("events_admin.language_hint")}
          </div>
        </div>

      </Card.Body>
    </Card>

    <CoverPickerModal
      isOpen={showCoverPicker}
      onClose={() => setShowCoverPicker(false)}
      event={event}
      onCoverSelected={(url) => {
        setCoverPreview(`${url}&t=${Date.now()}`);
        if (onCoverUpdate) onCoverUpdate(url);
      }}
    />
  </>
  );
};

export default EventDetails;
